import React, { useState } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	Typography,
	Box,
	Alert,
	CircularProgress
} from '@mui/material';
import { useMutation } from '@apollo/client';
import { IMPERSONATE_USER } from '../../gql/queries';
import { useAuthContext } from '../../contexts/auth-context';
import { toast } from 'react-toastify';

interface ImpersonateDialogProps {
	open: boolean;
	onClose: () => void;
}

const ImpersonateDialog: React.FC<ImpersonateDialogProps> = ({ open, onClose }) => {
	const [phoneNumber, setPhoneNumber] = useState('');
	const [error, setError] = useState<string | null>(null);
	const { impersonateUser } = useAuthContext();

	const [impersonateUserMutation, { loading }] = useMutation(IMPERSONATE_USER, {
		onCompleted: (data) => {
			// Store the new token and reload the page
			const token = data.impersonateUser;
			if (token) {
				impersonateUser(token);
				toast.success('Successfully impersonating user');
				onClose();
				// Reload to refresh the UI with the new user context
				window.location.reload();
			}
		},
		onError: (error) => {
			setError(error.message);
		}
	});

	const handleSubmit = () => {
		// Basic validation
		if (!phoneNumber || phoneNumber.trim().length < 10) {
			setError('Please enter a valid phone number');
			return;
		}

		setError(null);
		impersonateUserMutation({
			variables: { phoneNumber: phoneNumber.trim() }
		});
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Impersonate User</DialogTitle>
			<DialogContent>
				<Box sx={{ mt: 2 }}>
					<Typography variant="body1" sx={{ mb: 2 }}>
						Enter the phone number of the user you want to impersonate:
					</Typography>
					<TextField
						autoFocus
						label="Phone Number"
						type="tel"
						fullWidth
						value={phoneNumber}
						onChange={(e) => setPhoneNumber(e.target.value)}
						placeholder="Enter phone number"
						variant="outlined"
						disabled={loading}
					/>
					{error && (
						<Alert severity="error" sx={{ mt: 2 }}>
							{error}
						</Alert>
					)}
					<Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
						Note: You will be logged in as this user but retain your super admin privileges.
					</Typography>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					variant="contained"
					color="primary"
					disabled={loading}
				>
					{loading ? <CircularProgress size={24} /> : 'Impersonate'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ImpersonateDialog;
